import { useState } from "react";
import { ThemeProvider } from "next-themes";
import { useAuth } from "@/hooks/useAuth";
import { useShelterUpdates, useApproveUpdate, useRejectUpdate } from "@/hooks/useShelterUpdates";
import { AddAdminForm } from "@/components/AddAdminForm";
import { ChangePasswordForm } from "@/components/ChangePasswordForm";
import { AddShelterForm } from "@/components/AddShelterForm";
import { ShelterHistoryTab } from "@/components/admin/ShelterHistoryTab";
import { OrganizationVerificationTab } from "@/components/admin/OrganizationVerificationTab";
import { GraphicDownloadsTab } from "@/components/admin/GraphicDownloadsTab";
import { InstagramGraphicsTab } from "@/components/admin/InstagramGraphicsTab";
import { AnalyticsTab } from "@/components/admin/AnalyticsTab";
import { AdminHeader } from "@/components/admin/AdminHeader";
import { AdminStatsCards } from "@/components/admin/AdminStatsCards";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Loader2, LogOut, Check, X, Clock, MapPin, Mail, Phone, Dog, Cat, AlertTriangle, Users, FileText, Home, PlusCircle, MessageSquare, History, BadgeCheck, Image, Instagram, BarChart3 } from "lucide-react";
import { UpdateDiffView } from "@/components/UpdateDiffView";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { format } from "date-fns";
import { pl } from "date-fns/locale";
import { lovable } from "@/integrations/lovable";


const AdminPage = () => {
  const { user, isAdmin, loading, signIn, signOut } = useAuth();
  const { data: updates, isLoading: updatesLoading, refetch } = useShelterUpdates(!!user && isAdmin);
  const approveUpdate = useApproveUpdate();
  const rejectUpdate = useRejectUpdate();
  
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [signOutLoading, setSignOutLoading] = useState(false);


  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginLoading(true);
    try {
      await signIn(email, password);
      toast.success("Zalogowano pomyślnie");
      // Refetch after login to get data with new auth token
      setTimeout(() => refetch(), 500);
    } catch (error: any) {
      toast.error(error.message || "Błąd logowania");
    } finally {
      setLoginLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setGoogleLoading(true);
    try {
      const { error } = await lovable.auth.signInWithOAuth("google", {
        redirect_uri: window.location.origin,
      });
      if (error) {
        toast.error(error.message || "Błąd logowania przez Google");
      }
    } catch (error: any) {
      toast.error(error.message || "Błąd logowania przez Google");
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleSignOut = async () => {
    setSignOutLoading(true);
    try {
      // Najpierw spróbuj standardowego wylogowania.
      await signOut();

      // Fallback: agresywne czyszczenie lokalnej sesji (na wypadek problemów z domeną/cookies).
      try {
        const keysToRemove: string[] = [];
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key && key.includes("-auth-token")) keysToRemove.push(key);
        }
        keysToRemove.forEach((k) => localStorage.removeItem(k));
      } catch {
        // ignoruj
      }

      toast.success("Wylogowano");
      // Pełne odświeżenie, żeby UI na pewno zsynchronizował stan sesji.
      window.location.assign("/admin");
    } catch (error: any) {
      toast.error(error?.message || "Błąd wylogowania");
    } finally {
      setSignOutLoading(false);
    }
  };

  const handleApprove = async (update: any) => {
    try {
      await approveUpdate.mutateAsync(update);
      toast.success("Aktualizacja zatwierdzona");
    } catch (error: any) {
      toast.error(error.message || "Błąd zatwierdzania");
    }
  };

  const handleReject = async (updateId: string) => {
    try {
      await rejectUpdate.mutateAsync(updateId);
      toast.success("Zgłoszenie odrzucone");
    } catch (error: any) {
      toast.error(error.message || "Błąd odrzucania");
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  // Login form
  // Jeśli user jest zalogowany, ale nie ma roli admina, też pokazujemy logowanie (bez pętli wylogowywania).
  // Dzięki temu nie trzeba "czyścić ciasteczek" – można po prostu zalogować się innym kontem.
  if (!user || !isAdmin) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Panel Administratora</CardTitle>
            <CardDescription>
              {user && !isAdmin
                ? "To konto nie ma uprawnień administratora. Zaloguj się kontem admina."
                : "Zaloguj się, aby zarządzać schroniskami"}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {user && !isAdmin && (
              <div className="mb-4 rounded-lg border border-border bg-card p-3">
                <p className="text-sm font-medium">Zalogowano jako: {user.email}</p>
                <p className="mt-1 text-sm text-muted-foreground">
                  To konto nie ma dostępu do panelu. Możesz się wylogować albo zalogować innym kontem –
                  sesja zostanie podmieniona.
                </p>


                <div className="mt-3 flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full"
                    onClick={handleSignOut}
                    disabled={signOutLoading}
                  >
                    {signOutLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <LogOut className="w-4 h-4 mr-2" />
                    )}
                    Wyloguj
                  </Button>
                </div>
              </div>
            )}
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@example.com"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Hasło</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>
              <Button type="submit" className="w-full" disabled={loginLoading}>
                {loginLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Zaloguj się
              </Button>
              
              <div className="relative my-4">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-background px-2 text-muted-foreground">lub</span>
                </div>
              </div>

              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={handleGoogleSignIn}
                disabled={googleLoading}
              >
                {googleLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24">
                    <path
                      fill="currentColor"
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    />
                    <path
                      fill="currentColor"
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    />
                    <path
                      fill="currentColor"
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    />
                    <path
                      fill="currentColor"
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    />
                  </svg>
                )}
                Zaloguj przez Google
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Admin dashboard
  return (
    <ThemeProvider attribute="class" defaultTheme="light" enableSystem={false}>
    <div className="min-h-screen bg-background">
      <AdminHeader email={user.email!} signOutLoading={signOutLoading} onSignOut={handleSignOut} />

      <main className="px-6 py-8 max-w-4xl mx-auto space-y-8">
        <div className="hidden md:block">
          <AdminStatsCards pendingUpdatesCount={updates?.length ?? 0} />
        </div>

        <hr className="hidden md:block border-border/50" />

        <Tabs defaultValue="updates" className="space-y-8">
          <TabsList className="flex w-full h-auto gap-0.5 p-1.5 bg-muted/50 rounded-xl">
            <TabsTrigger value="updates" className="flex-1 gap-1 px-1.5 py-2 rounded-lg data-[state=active]:shadow-md text-xs">
              <FileText className="w-3.5 h-3.5 shrink-0" />
              <span className="hidden sm:inline">Zgłoszenia</span>
            </TabsTrigger>
            <TabsTrigger value="organizations" className="flex-1 gap-1 px-1.5 py-2 rounded-lg data-[state=active]:shadow-md text-xs">
              <BadgeCheck className="w-3.5 h-3.5 shrink-0" />
              <span className="hidden sm:inline">Weryfikacja</span>
            </TabsTrigger>
            <TabsTrigger value="analytics" className="flex-1 gap-1 px-1.5 py-2 rounded-lg data-[state=active]:shadow-md text-xs">
              <BarChart3 className="w-3.5 h-3.5 shrink-0" />
              <span className="hidden sm:inline">Analityka</span>
            </TabsTrigger>
            <TabsTrigger value="graphics" className="flex-1 gap-1 px-1.5 py-2 rounded-lg data-[state=active]:shadow-md text-xs">
              <Image className="w-3.5 h-3.5 shrink-0" />
              <span className="hidden sm:inline">Grafiki</span>
            </TabsTrigger>
            <TabsTrigger value="instagram" className="flex-1 gap-1 px-1.5 py-2 rounded-lg data-[state=active]:shadow-md text-xs">
              <Instagram className="w-3.5 h-3.5 shrink-0" />
              <span className="hidden sm:inline">Instagram</span>
            </TabsTrigger>
            <TabsTrigger value="history" className="flex-1 gap-1 px-1.5 py-2 rounded-lg data-[state=active]:shadow-md text-xs">
              <History className="w-3.5 h-3.5 shrink-0" />
              <span className="hidden sm:inline">Historia</span>
            </TabsTrigger>
            <TabsTrigger value="shelters" className="flex-1 gap-1 px-1.5 py-2 rounded-lg data-[state=active]:shadow-md text-xs">
              <PlusCircle className="w-3.5 h-3.5 shrink-0" />
              <span className="hidden sm:inline">Nowe</span>
            </TabsTrigger>
            <TabsTrigger value="admins" className="flex-1 gap-1 px-1.5 py-2 rounded-lg data-[state=active]:shadow-md text-xs">
              <Users className="w-3.5 h-3.5 shrink-0" />
              <span className="hidden sm:inline">Admini</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="updates" className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="font-display font-semibold text-lg">Oczekujące zgłoszenia</h2>
              <Badge variant="secondary">
                {updates?.length || 0} oczekujących
              </Badge>
            </div>

            {updatesLoading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : updates && updates.length > 0 ? (
              <div className="space-y-4">
                {updates.map((update, index) => {
                  const isOrgRequest = (update as any).is_organization_request;
                  const isNew = !update.shelter_id;
                  const borderColor = isOrgRequest
                    ? "border-l-primary"
                    : isNew
                    ? "border-l-secondary"
                    : "border-l-muted-foreground/30";

                  return (
                  <Card
                    key={update.id}
                    className={`border-l-4 ${borderColor} animate-fade-in transition-shadow hover:shadow-md`}
                    style={{ animationDelay: `${index * 60}ms`, animationFillMode: "both" }}
                  >
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <div>
                          <CardTitle className="text-lg flex items-center gap-2 flex-wrap">
                            {update.shelter_name}
                            {(update as any).is_organization_request && (
                              <Badge variant="default" className="bg-primary/90 text-primary-foreground">
                                <BadgeCheck className="w-3 h-3 mr-1" />
                                Prośba o weryfikację
                              </Badge>
                            )}
                          </CardTitle>
                          <CardDescription className="flex items-center gap-2 mt-1">
                            <MapPin className="w-4 h-4" />
                            {update.city}{update.region && `, ${update.region}`}
                          </CardDescription>
                        </div>
                        <div className="flex flex-col items-end gap-2">
                          <Badge variant={update.shelter_id ? "secondary" : "default"}>
                            {update.shelter_id ? "Aktualizacja" : "Nowe schronisko"}
                          </Badge>
                          <span className="text-xs text-muted-foreground flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {format(new Date(update.created_at), "d MMM yyyy, HH:mm", { locale: pl })}
                          </span>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        {/* Diff view showing changes */}
                        <UpdateDiffView update={update} />

                        {/* Submitter note */}
                        {update.submitter_note && (
                          <div className="bg-amber-50/40 dark:bg-amber-950/10 border border-amber-200/40 dark:border-amber-800/30 rounded-lg p-3">
                            <p className="text-sm font-medium mb-1 flex items-center gap-1.5 text-amber-800/60 dark:text-amber-200/60">
                              <MessageSquare className="w-4 h-4" />
                              Komentarz od zgłaszającego:
                            </p>
                            <p className="text-sm text-amber-700/60 dark:text-amber-300/60 whitespace-pre-wrap">
                              {update.submitter_note}
                            </p>
                          </div>
                        )}
                        <div className="flex gap-2 pt-2">
                          <Button
                            onClick={() => handleApprove(update)}
                            disabled={approveUpdate.isPending}
                            className="flex-1"
                          >
                            {approveUpdate.isPending ? (
                              <Loader2 className="w-4 h-4 animate-spin mr-2" />
                            ) : (
                              <Check className="w-4 h-4 mr-2" />
                            )}
                            Zatwierdź
                          </Button>
                          <Button
                            onClick={() => handleReject(update.id)}
                            disabled={rejectUpdate.isPending}
                            variant="destructive"
                            className="flex-1"
                          >
                            {rejectUpdate.isPending ? (
                              <Loader2 className="w-4 h-4 animate-spin mr-2" />
                            ) : (
                              <X className="w-4 h-4 mr-2" />
                            )}
                            Odrzuć
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  );
                })}
              </div>
            ) : (
              <Card className="animate-fade-in">
                <CardContent className="py-12 text-center text-muted-foreground">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center">
                    <Check className="w-8 h-8 text-primary" />
                  </div>
                  <p className="font-semibold text-foreground">Brak oczekujących zgłoszeń</p>
                  <p className="text-sm mt-1">Wszystkie aktualizacje zostały przejrzane</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="organizations">
            <OrganizationVerificationTab />
          </TabsContent>

          <TabsContent value="analytics">
            <AnalyticsTab />
          </TabsContent>

          <TabsContent value="graphics">
            <GraphicDownloadsTab />
          </TabsContent>

          <TabsContent value="instagram">
            <InstagramGraphicsTab />
          </TabsContent>

          <TabsContent value="history">
            <ShelterHistoryTab />
          </TabsContent>

          <TabsContent value="shelters">
            <AddShelterForm />
          </TabsContent>

          <TabsContent value="admins" className="space-y-6">
            <ChangePasswordForm />
            <AddAdminForm />
          </TabsContent>
        </Tabs>
      </main>
    </div>
    </ThemeProvider>
  );
};

export default AdminPage;
